import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import {
  Typography,
  Button,
  Box,
  MenuItem,
  FormControl,
  Grid
} from "@mui/material";
import { TextField, CheckboxWithLabel, Select } from "formik-mui";
import { Field, useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import Modal from "../Modal/Modal";
import QuestionTypeFields from "../CourseBuilder/EditQuestion/QuestionTypeFields";
import QuestionPreview from "../CourseBuilder/EditQuestion/QuestionPreviewModal";
import QuestionTypeMenu from "./QuestionTypeMenu";
import { quizQuestionTypes, surveyQuestionTypes } from "../../constants";

const quizTypes = Object.values(quizQuestionTypes);
const surveyTypes = Object.values(surveyQuestionTypes);

const EditQuestionTypeField = ({ t, onChange, isSurvey }) => {
  const questionTypes = isSurvey ? surveyTypes : quizTypes;
  return (
    <FormControl fullWidth>
      <Field
        type="text"
        name="questionType"
        component={Select}
        inputProps={{
          name: "questionType",
          id: "edit-question-type-field",
          onChange: ({ target: { value } }) => onChange(value)
        }}
        variant="filled"
        fullWidth
        renderValue={value => t(`questionBank.questionTypes.${value}`)}
        hiddenLabel
      >
        {questionTypes.map(questionType => (
          <MenuItem value={questionType} key={questionType}>
            {t(`questionBank.questionTypes.${questionType}`)}
          </MenuItem>
        ))}
      </Field>
    </FormControl>
  );
};

const EditQuestionPointsField = ({ t, isSurveyQuestion }) => {
  if (isSurveyQuestion) {
    return null;
  } else {
    return (
      <Field
        label={t("editQuestion.pointsLabel")}
        fullWidth
        variant="filled"
        component={TextField}
        name="points"
        InputProps={{
          inputProps: {
            type: "number",
            min: "1",
            "data-qa": "question-type-points-field",
            id: "question-type-points-field"
          }
        }}
      />
    );
  }
};

const EditQuestionMandatoryCheckbox = ({ t }) => (
  <Box ml={2}>
    <Field
      component={CheckboxWithLabel}
      name="mandatory"
      inputProps={{
        "data-qa": "question-type-mandatory-field"
      }}
      Label={{
        label: (
          <Typography variant="subtitle1">
            {t("editQuestion.mandatoryLabel")}
          </Typography>
        )
      }}
      color="primary"
      type="checkbox"
    />
  </Box>
);

const EditQuestionModalTitle = ({ isNewQuestion, onPreviewQuestion }) => {
  const { t } = useTranslation();
  return (
    <Box display="flex" justifyContent="space-between">
      <Typography variant="h4" color="textPrimary">
        {isNewQuestion
          ? t("editQuestion.modalTitleCreateQuestion")
          : t("editQuestion.modalTitleEditQuestion")}
      </Typography>
      <Button variant="outlined" onClick={onPreviewQuestion} color="primary">
        {t("editQuestion.previewQuestionBtn")}
      </Button>
    </Box>
  );
};

const EditQuestionModal = ({
  onSubmit,
  onClose,
  open,
  isNewQuestion,
  isSurveyQuestion,
  onQuestionTypeChange,
  onAnswersChange,
  questionFilesList,
  setQuestionFilesList
}) => {
  const { t } = useTranslation();

  const { values, isSubmitting, resetForm, setFieldValue } = useFormikContext();

  useEffect(() => {
    resetForm();
  }, [open, values.questionType]);

  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  return (
    <Modal
      scrollable
      disableEnforceFocus
      maxWidth="lg"
      fullWidth
      fillHeight
      dialogTitle={
        <EditQuestionModalTitle
          isNewQuestion={isNewQuestion}
          onPreviewQuestion={() => setPreviewModalOpen(true)}
        />
      }
      dialogContent={
        <Box display="flex" flexDirection="column" py={1} height="100%">
          <Box display="flex">
            <Grid container spacing={3} alignItems="center" flex={1}>
              <Grid item xs={12} sm={4}>
                <EditQuestionTypeField
                  t={t}
                  onChange={onQuestionTypeChange}
                  isSurvey={isSurveyQuestion}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <EditQuestionPointsField
                  t={t}
                  isSurveyQuestion={isSurveyQuestion}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <EditQuestionMandatoryCheckbox t={t} />
              </Grid>
            </Grid>
          </Box>
          <QuestionTypeFields
            questionType={values.questionType}
            answers={values.answers}
            onAnswersChange={onAnswersChange}
            isSurveyQuestion={isSurveyQuestion}
            questionFilesList={questionFilesList}
            setQuestionFilesList={setQuestionFilesList}
          />
          <QuestionPreview
            label={t("editQuestion.previewLabel")}
            open={previewModalOpen}
            onClose={() => setPreviewModalOpen(false)}
            questionType={values.questionType}
            isSurveyQuestion={isSurveyQuestion}
          />
        </Box>
      }
      dialogActions={
        <>
          <Button color="primary" disabled={isSubmitting} onClick={onClose}>
            {t("editQuestion.cancelBtn")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setFieldValue("saveAndCreateNewQuestionType", "");
              onSubmit();
            }}
            disabled={isSubmitting}
          >
            {t("editQuestion.saveBtn")}
          </Button>
          <QuestionTypeMenu
            label={t("editQuestion.saveAndCreateNewBtn")}
            onSelect={newQuestionType => {
              setFieldValue("saveAndCreateNewQuestionType", newQuestionType);
              onSubmit();
            }}
            isSurvey={isSurveyQuestion}
          />
        </>
      }
      open={open}
      onClose={onClose}
    />
  );
};

EditQuestionModal.propTypes = {
  /**
   * Called to close the Modal
   */
  onClose: PropTypes.func,
  /**
   * True if the modal is open, false otherwise
   */
  open: PropTypes.bool,
  onSubmit: PropTypes.func,
  isSurveyQuestion: PropTypes.bool,
  onQuestionTypeChange: PropTypes.func,
  onAnswersChange: PropTypes.func,
  isNewQuestion: PropTypes.bool
};

export default EditQuestionModal;
